import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import AsyncTable, { Column, RequestPage } from '../../../../components/AsyncTable';
import NewRegisterBtn from '../../../../components/Buttons/NewRegisterBtn';
import Modal from '../../../../components/Modal';
import { backendErrorNotification, frontendNotification } from '../../../../components/Notification';
import { ActionButtonsContainer, DeleteIconTable, EditIconTable } from '../../../../components/TablePaginator/styles';
import { tableExtensionsApi, tableExtensionOptionsApi } from '../../../../services/requests';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import checkPermission from '../../../../utils/checkPermission';
import TableExtensionModal from './components/TableExtensionModal';
import { ExtensionTable, TableExtension } from './types';
import './TableExtensions.css';

const TableExtensionsPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [rows, setRows] = useState<TableExtension[]>([]);
  const [tables, setTables] = useState<ExtensionTable[]>([]);
  const [tableName, setTableName] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [revision, setRevision] = useState(0);
  const [editing, setEditing] = useState<TableExtension | null | undefined>(undefined);
  const [confirmation, setConfirmation] = useState<TableExtension | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    tableExtensionOptionsApi.query = '';
    tableExtensionOptionsApi.index().then((response) => { if (active) setTables(response.data.data.tables); })
      .catch((error: AxiosError) => { if (active) backendErrorNotification(error); });
    return () => { active = false; };
  }, []);

  const requestPage: RequestPage = useCallback(async ({ search, page, quantityPerPage }) => {
    try {
      const query = new URLSearchParams({ search, page: String(page), quantityPerPage: String(quantityPerPage),
        table_name: tableName, include_deleted: String(includeDeleted) });
      tableExtensionsApi.query = `?${query}`;
      const response = await tableExtensionsApi.index();
      return { data: response.data.data, hasMore: page < response.data.paginated.totalPage,
        totalPage: response.data.paginated.totalPage };
    } catch (error) {
      backendErrorNotification(error as AxiosError);
      return { data: [], hasMore: false, totalPage: 0 };
    }
  }, [tableName, includeDeleted]);

  const confirm = async () => {
    if (!confirmation || saving) return;
    setSaving(true);
    try {
      tableExtensionsApi.query = '';
      if (confirmation.deleted_at) {
        const { getApi } = await import('../../../../services/api');
        const { databaseManagement } = await import('../../../../services/endpoints');
        const api = await getApi();
        await api.post(`${databaseManagement['/table-extensions']}/${confirmation.id}/restore`);
      } else await tableExtensionsApi.delete(confirmation.id);
      frontendNotification({ message: t(confirmation.deleted_at ? 'tableExtensionRestored' : 'successfullyDeleted'),
        type: 'success' });
      setConfirmation(null);
      setRevision((value) => value + 1);
    } catch (error) { backendErrorNotification(error as AxiosError); }
    finally { setSaving(false); }
  };

  const columns: Column<TableExtension>[] = [
    { label: t('table'), accessor: 'table_name' },
    { label: t('tableExtensionFieldName'), accessor: 'field_name' },
    { label: t('type'), Cell: (row) => t(`tableExtensionTypes.${row.field_type}`) },
    { label: t('description'), accessor: 'description' },
    { label: t('tableExtensionRequired'), Cell: (row) => t(row.required ? 'yes' : 'no') },
    { label: t('status'), Cell: (row) => t(row.deleted_at ? 'tableExtensionDeleted' : 'active') },
    { label: t('actions'), Cell: (row) => (
      <ActionButtonsContainer type={2}>
        {!row.deleted_at && checkPermission('tableExtension-edit') && (
          <button type="button" className="table-extension-icon-action" title={t('edit')}
            aria-label={t('edit')} onClick={() => setEditing(row)}><EditIconTable /></button>
        )}
        {!row.deleted_at && checkPermission('tableExtension-delete') && (
          <button type="button" className="table-extension-icon-action" title={t('delete')}
            aria-label={t('delete')} onClick={() => setConfirmation(row)}><DeleteIconTable /></button>
        )}
        {row.deleted_at && checkPermission('tableExtension-edit') && (
          <button type="button" className="input" onClick={() => setConfirmation(row)}>{t('tableExtensionRestore')}</button>
        )}
      </ActionButtonsContainer>
    ) },
  ];

  return (
    <ContentContainer title={t('tableExtensions')}>
      <p className="table-extension-note">{t('tableExtensionsIntro')}</p>
      {checkPermission('tableExtension-create') && (
        <NewRegisterBtn onClick={() => setEditing(null)} style={{ marginLeft: 0, marginBottom: '1rem' }} />
      )}
      <div className="table-extension-filters">
        <label htmlFor="extension-table-filter">{t('table')}</label>
        <select id="extension-table-filter" className="input" value={tableName}
          onChange={(event) => setTableName(event.target.value)}>
          <option value="">{t('all')}</option>
          {tables.map((table) => <option key={table.table_name} value={table.table_name}>{t(table.label)}</option>)}
        </select>
        <label className="table-extension-checkbox" htmlFor="extension-deleted-filter">
          <input id="extension-deleted-filter" type="checkbox" checked={includeDeleted}
            onChange={(event) => setIncludeDeleted(event.target.checked)} />{t('tableExtensionIncludeDeleted')}
        </label>
      </div>
      <AsyncTable tableName={t('tableExtensions')} columns={columns} value={rows} onChange={setRows}
        requestPage={requestPage} reqListener={[revision, tableName, includeDeleted]}
        options={{ styles: { primaryColor: theme.colors.surface, secondaryColor: theme.colors.onSurface,
          alternateRowColor: theme.colors.onSurfaceLight, textColor: theme.colors.text },
        quantityPerPageLabel: t('quantityPerPage'), searchPlaceholder: t('tableExtensionSearch') }} />
      {editing !== undefined && (
        <TableExtensionModal item={editing} close={() => setEditing(undefined)}
          onSuccess={() => setRevision((value) => value + 1)} />
      )}
      {confirmation && (
        <Modal title={t(confirmation.deleted_at ? 'tableExtensionRestore' : 'delete')} showModal
          closeModal={() => setConfirmation(null)} cancelButton={() => setConfirmation(null)} action={confirm}
          actionNameBtn={t(confirmation.deleted_at ? 'tableExtensionRestore' : 'delete')} isLoading={saving}
          bodyStyle={{ padding: '5rem 1rem', boxSizing: 'border-box' }}>
          <p><strong>{confirmation.table_name}.{confirmation.field_name}</strong></p>
          <p>{t(confirmation.deleted_at ? 'tableExtensionRestoreHint' : 'tableExtensionDeleteHint')}</p>
        </Modal>
      )}
    </ContentContainer>
  );
};

export default TableExtensionsPage;
